import cx from 'classnames';
import { Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { FunctionComponent, VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { PACKAGE_INVENTORY, RAW_VULN_EDIT, SBOM_PORTAL } from '../../../shared/featureFlags/flagNames';
import { Category } from '../../../shared/issuesV2';
import { isFreeTier } from '../../../shared/subscriptions';
import { BannerPlacement } from '../../../typings/enums';
import { Action } from '../../../typings/permissions';
import { window } from '../../globals';
import { isFeatureEnabled, isQualityEnabled, isSecurityEnabled, useIsFeatureEnabled } from '../../helpers/features';
import { getCategoryLabel, permittedIssueCategoriesToView } from '../../helpers/issuesV2';
import {
  userHasPermissionForPolicyType,
  userHasPermissionToCreateOrgReports,
  userHasPermissionToCreateTeamReports,
} from '../../helpers/permissions';
import NavigationStore from '../../redux/navigation';
import * as ProjectsV2Store from '../../redux/projectsV2';
import { PolicyType } from '../../types';
import BannerManager from '../BannerManager';
import { FeatureFlagRequired } from '../generic';
import AnnouncementBanner from '../generic/Banners/AnnouncementBanner';
import EmailVerificationBanner from '../generic/Banners/EmailVerificationBanner';
import MasqueradeBanner from '../generic/Banners/MasqueradeBanner';
import OrgAlertBanner from '../generic/Banners/OrgAlertBanner';
import FOSSAGlyph from '../generic/FOSSAInlineSVG/FOSSAGlyph';
import PaidFeatureGate from '../generic/Gates/PaidFeatureGate';
import PortalUrlOnlyGate from '../generic/Gates/PortalUrlOnlyGate';
import { Link, RouterLink } from '../generic/Link';
import { orgHasIssueOverviewEnabled } from '../issueOverview/accessControl';
import { issueOverviewFeature } from '../settings/billing/features/consts';
import type { RouterProps } from '../../types';

import AccountDropdown from './AccountDropdown';
import MenuItemLink from './MenuItemLink';
import SBOMPortalLinks from './SBOMPortalLinks';
import SupportDropdown from './SupportDropdown';

import styles from './Navigation.module.less';

// Only paths that start with /projects or /release-groups are considered.
// This is to avoid conflicts with other paths that contain /projects or /release-groups in them (e.g. /account/settings/projects/general).
const projectsPagePath = /^\/projects\/?/;
const releaseGroupsPagePath = /^\/release-groups\/?/;

const getDefaultPoliciesPage = () => {
  if (userHasPermissionForPolicyType(Action.View, PolicyType.LICENSING)) {
    return Category.Licensing;
  }
  if (
    isSecurityEnabled() &&
    userHasPermissionForPolicyType(Action.View, PolicyType.SECURITY)
  ) {
    return Category.Vulnerability;
  }
  if (
    isQualityEnabled() &&
    userHasPermissionForPolicyType(Action.View, PolicyType.QUALITY)
  ) {
    return Category.Quality;
  }
  return null;
};

type IssuesNavItemProps = {
  routerPush: InjectedRouter['push'];
  isOnGlobalIssues: boolean;
};

/**
 * The Issues nav dropdown item.
 *
 * @param {InjectedRouter['push']} routerPush - router.push
 * @param {boolean} isOnGlobalIssues - Whether the current path is the global issues page.
 */
const IssuesNavItem: VoidFunctionComponent<IssuesNavItemProps> = ({ routerPush, isOnGlobalIssues }) => {
  const permittedIssueCategories = permittedIssueCategoriesToView();
  const isIssueOverviewEnabled = orgHasIssueOverviewEnabled();

  return (
    permittedIssueCategories.length > 0 && (
      <li className="nav-right link-dropdown-right" key="nav-link-issues">
        <Dropdown id="packages-dropdown" pullRight>
          <Dropdown.Toggle className={isOnGlobalIssues ? 'active' : ''} useAnchor />
          <Dropdown.Menu className="nav-issues-dropdown">
            <PaidFeatureGate
              isRestricted={isFreeTier({
                plan: window.user?.organization?.stripe_plan,
                stripePriceLimit: window.user?.organization?.stripePriceLimit,
              })}
              modalFocusFeature={issueOverviewFeature}
            >
              <MenuItemLink key="overview" eventKey="/issues/overview" routerPush={routerPush}>
                Overview
              </MenuItemLink>
            </PaidFeatureGate>
            {permittedIssueCategories.map((category) => (
              <MenuItemLink key={category} eventKey={`/issues/${category}`} routerPush={routerPush}>
                {getCategoryLabel(category)}
              </MenuItemLink>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <RouterLink
          className={isOnGlobalIssues ? 'active' : ''}
          activeClassName="active"
          to={isIssueOverviewEnabled ? '/issues/overview' : `/issues/${permittedIssueCategories[0]}`}
        >
          Issues
        </RouterLink>
      </li>
    )
  );
};

type AdminNavItemProps = {
  routerPush: InjectedRouter['push'];
  pathname: string;
};

/**
 * Projects navigation link with query parameters
 */
const ProjectsNavItem: VoidFunctionComponent = () => {
  const queryString = useSelector(ProjectsV2Store.selectors.getProjectsQueryString);

  return (
    <li className="nav-right" key="nav-link-projects">
      <RouterLink activeClassName="active" to={`/projects${queryString}`}>
        Projects
      </RouterLink>
    </li>
  );
};

/**
 * Dropdown item for all of the admin/super user nav items.
 */
const AdminNavItem: VoidFunctionComponent<AdminNavItemProps> = ({ routerPush, pathname }) => {
  if (!window.user) {
    return null;
  }
  const isSuper = window.user.super;
  const hasRawVulnsEnabled = isFeatureEnabled(RAW_VULN_EDIT);
  if (!isSuper && !hasRawVulnsEnabled) {
    return null;
  }

  //  Kirill's path, since he isn't a super user but has RawVulns access.
  if (hasRawVulnsEnabled && !isSuper) {
    return (
      <li className="nav-right">
        <RouterLink activeClassName="active" to="/raw-vulns">
          Vulns
        </RouterLink>
      </li>
    );
  }

  const isOnAdminPage = [
    pathname.startsWith('/admin'),
    pathname.startsWith('/internal-tools'),
    pathname.startsWith('/raw-vulns'),
  ].some(isOnPath => !!isOnPath);

  return (
    <li className="nav-right link-dropdown-right" key="nav-link-admin">
      <Dropdown id="admin-nav-dropdown" pullRight>
        <Dropdown.Toggle className={isOnAdminPage ? 'active' : ''} useAnchor />
        <Dropdown.Menu className="nav-issues-dropdown">
          <MenuItemLink key="admin" eventKey="/admin" routerPush={routerPush}>
            Admin
          </MenuItemLink>
          <MenuItemLink key="internal-tools" eventKey="/internal-tools" routerPush={routerPush}>
            Internal Tools
          </MenuItemLink>
          {hasRawVulnsEnabled && (
            <MenuItemLink key="vulns" eventKey="/raw-vulns" routerPush={routerPush}>
              Vulns
            </MenuItemLink>
          )}
        </Dropdown.Menu>
      </Dropdown>
      <RouterLink className={isOnAdminPage ? 'active' : ''} activeClassName="active" to="/admin">
        Admin
      </RouterLink>
    </li>
  );
};

type NavigationProps = RouterProps & {
  routes?: any;
  router: InjectedRouter;
};

const Navigation: FunctionComponent<NavigationProps> = ({ params, location, routes, router }) => {
  const state = useSelector(NavigationStore.selectors.get);
  const Subnav = state.subnav;

  const doesPathIncludesProjects = location.pathname.includes('/projects/');
  const isOnProjects = location.pathname.match(projectsPagePath) || location.pathname.match(releaseGroupsPagePath);
  const isOnPackages = location.pathname.includes('/packages') && !doesPathIncludesProjects;
  const isOnGlobalIssues = location.pathname.startsWith('/issues') && !doesPathIncludesProjects;
  const defaultPoliciesPage = getDefaultPoliciesPage();

  const isPackageInventoryEnabled = useIsFeatureEnabled(PACKAGE_INVENTORY);

  return (
    <header
      className={cx('dashboard-navigation-wrapper', {
        //  When this is set (in `useCallToAction`), the nav will not render with a border-bottom (the CTA manages that instead).
        'dashboard-navigation-wrapper--cta': state.navProps?.shouldUseCallToAction,
      })}
    >
      <OrgAlertBanner />
      <MasqueradeBanner />
      <AnnouncementBanner />
      <EmailVerificationBanner />
      {window.on_prem && isOnProjects && <BannerManager placement={BannerPlacement.Dashboard} />}
      <div className={cx('dashboard-navigation container container-responsive nomargin-top', state.expand)}>
        <nav className={styles.navigation}>
          {window.user ? (
            <RouterLink to="/" className={styles.logo}>
              <FOSSAGlyph width={40} />
            </RouterLink>
          ) : (
            <Link className={styles.logo} to="https://fossa.com" openInNewTab>
              <FOSSAGlyph width={40} /> <span className="badge label-knockout">Public Index</span>
            </Link>
          )}
          <ul className="category-navigation">
            <li className="nav-right nav-hover">
              <AccountDropdown user={window.user} router={router} />
            </li>
            <SupportDropdown />
            {defaultPoliciesPage && (
              <li className="nav-right" key="nav-link-policies">
                <RouterLink activeClassName="active" to={`/policies/${defaultPoliciesPage}`}>
                  Policies
                </RouterLink>
              </li>
            )}
            {(userHasPermissionToCreateOrgReports() || userHasPermissionToCreateTeamReports()) && (
              <li className="nav-right" key="nav-link-components">
                <RouterLink activeClassName="active" to="/reports">
                  Reports
                </RouterLink>
              </li>
            )}
            {isPackageInventoryEnabled && (
              <li className="nav-right" key="nav-link-packages">
                <RouterLink className={isOnPackages ? 'active' : ''} activeClassName="active" to="/packages">
                  Packages
                </RouterLink>
              </li>
            )}
            <FeatureFlagRequired flagName={SBOM_PORTAL}>
              <PortalUrlOnlyGate>
                <SBOMPortalLinks pathname={location.pathname} routerPush={router.push} />
              </PortalUrlOnlyGate>
            </FeatureFlagRequired>
            <IssuesNavItem routerPush={router.push} isOnGlobalIssues={isOnGlobalIssues} />
            {window.user && <ProjectsNavItem />}
            <AdminNavItem pathname={location.pathname} routerPush={router.push} />
          </ul>
        </nav>

        {Subnav && (
          // @ts-expect-error We can't know for sure what the props are going to be for the element, so this will always complain. We define the type as a React Component.
          <Subnav
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...state.navProps}
            expand={state.expand}
            params={params}
            routes={routes}
            location={location}
            router={router}
          />
        )}
      </div>
    </header>
  );
};

export default Navigation;
